package com.yogaaicoach.pages.payment.ui

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.yogaaicoach.pages.auth.TokenDataManager
import com.yogaaicoach.pages.payment.data.CreateSessionRequest
import com.yogaaicoach.pages.payment.data.PaymentApi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class SubscriptionViewModel(private val paymentApi: PaymentApi) : ViewModel() {
    var loading by mutableStateOf(false)
        private set
    var url by mutableStateOf<String?>(null)
        private set

    var error by mutableStateOf<String?>(null)
        private set

    // StateFlow variants for jsMain React collectors.
    private val _loadingFlow = MutableStateFlow(false)
    val loadingFlow: StateFlow<Boolean> = _loadingFlow.asStateFlow()

    private val _errorFlow = MutableStateFlow<String?>(null)
    val errorFlow: StateFlow<String?> = _errorFlow.asStateFlow()

    private val _selectedPlan = MutableStateFlow<SubscriptionPlan?>(null)
    val selectedPlan: StateFlow<SubscriptionPlan?> = _selectedPlan.asStateFlow()

    val trialDaysRemaining = TokenDataManager.tokenData?.freeTrialDaysLeft

    fun onPlanSelected(plan: SubscriptionPlan) {
        _selectedPlan.update { plan }
    }

    /**
     * Entry point for the subscribe button. Android must use Google Play Billing to stay within
     * Play's payments policy; every other platform goes through Stripe Checkout.
     */
    fun startSubscription() {
        if (usesPlayBilling) startPlayBillingPurchase() else createStripeSession()
    }

    private fun startPlayBillingPurchase() {
        val currentPlan = selectedPlan.value ?: return

        viewModelScope.launch {
            loading = true; _loadingFlow.value = true
            error = null; _errorFlow.value = null

            val result = launchPlayBillingPurchase(currentPlan)

            loading = false; _loadingFlow.value = false
            when (result) {
                is PlayBillingResult.Success -> {
                    val verified = paymentApi.verifyGooglePlayPurchase(result.purchaseToken)
                    if (verified?.subscribed != true) {
                        showError("We could not confirm your purchase. Please contact us if you were charged.")
                    }
                }
                // The user backing out of the Play sheet is not an error worth showing.
                PlayBillingResult.Cancelled -> Unit
                is PlayBillingResult.Failed -> showError(result.message)
            }
        }
    }

    private fun showError(message: String) {
        error = message
        _errorFlow.value = message
    }

    fun createStripeSession() {
        val currentPlan = selectedPlan.value ?: return

        viewModelScope.launch {
            loading = true; _loadingFlow.value = true
            error = null; _errorFlow.value = null

            val successUrl = "${Config.BASE_URL}?stripe=success"
            val cancelUrl = "${Config.BASE_URL}?stripe=cancel"

            val requestBody = CreateSessionRequest(successUrl, cancelUrl, currentPlan.name)

            val response = paymentApi.createStripeSession(requestBody)
            loading = false; _loadingFlow.value = false
            if (response == null) {
                error = "Unknown error happened! Please try again!"
                _errorFlow.value = "Unknown error happened! Please try again!"
            } else {
                stripeRedirect(response.url)
            }
        }
   }

    fun reset() {
        loading = false; _loadingFlow.value = false
        error = null; _errorFlow.value = null
        _selectedPlan.value = null
    }

}

expect fun stripeRedirect(url: String)

/** True only where store billing is mandatory (Android). */
expect val usesPlayBilling: Boolean

/**
 * Runs the Google Play purchase sheet and returns its outcome. The token it yields still has to be
 * verified by the backend before the user counts as subscribed.
 */
expect suspend fun launchPlayBillingPurchase(plan: SubscriptionPlan): PlayBillingResult

sealed interface PlayBillingResult {
    data class Success(val purchaseToken: String) : PlayBillingResult
    data object Cancelled : PlayBillingResult
    data class Failed(val message: String) : PlayBillingResult
}

/** Deep link to the Play Store, where Play subscriptions have to be cancelled. */
expect fun openPlayStoreSubscriptions()